import CountrySelection from 'components/forms/CountrySelection';
import Input from 'components/forms/Input';
import Radio from 'components/forms/Radio';
import RadioGroup from 'components/forms/RadioGroup';
import ArrowButton from 'components/Misc/ArrowButton';
import ErrorMessage from 'components/Misc/ErrorMessage';
import LoadingSpinner from 'components/Misc/LoadingSpinner';
import LoadingSpinnerWrapper from 'components/Misc/LoadingSpinnerWrapper';
import {
  createCartObject,
  getCart,
  Address,
  changeAddresses,
  ShippingMethods,
} from 'lib/commerce-client';
import { filterCustomerProfile } from 'lib/user-client';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import Steps from '../Steps';

type Inputs = {
  billingAddress: Address;
  shippingAddress: Address;
};

const isShippingAddressNeeded = (cart) => {
  return (
    cart?.order?.attributes?.shipping_method === ShippingMethods.Postversand ||
    cart?.order?.attributes?.shipping_method ===
      ShippingMethods['Gratis Postversand']
  );
};

export default function AddressPage() {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShippingAddressForm, setShowShippingAddressForm] = useState(false);
  const router = useRouter();
  const intl = useIntl();
  const [defaultCustomerProfile, setDefaultCustomerProfile] =
    useState<Address | null>(null);
  const [cart, setCart] = useState(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<Inputs>();
  const { locale } = useRouter();

  const loadCart = async (locale: string) => {
    setLoading(true);
    const json = await getCart(locale);
    if (!json) {
      setLoading(false);
      return;
    }
    const cartObject = createCartObject(json);
    setCart(cartObject);
    setShowShippingAddressForm(isShippingAddressNeeded(cartObject));
  };

  const REQUIRED_ERROR = intl.formatMessage({
    defaultMessage: 'Dies ist ein Pflichtfeld',
    id: 'g9K0Ij',
  });
  const EMAIL_ERROR = intl.formatMessage({
    defaultMessage: 'Keine gültige E-Mail Adresse',
    id: 'FMymm6',
  });
  const POSTAL_CODE_ERROR = intl.formatMessage({
    defaultMessage: 'Keine gültige Postleitzahl',
    id: 'yQ843C',
  });

  useEffect(() => {
    loadCart(locale);
  }, [locale]);

  useEffect(() => {
    // reset the formState before loading the profile, otherwise the initial empty formState will be cached.
    reset();
    // TT-713: Es gibt nur noch Gastbestellungen, die Adresse kommt deshalb
    // immer aus der Rechnungsadresse des Warenkorbs.
    const unfilteredProfile = cart?.order?.attributes?.billing_information;
    if (unfilteredProfile) {
      setDefaultCustomerProfile(filterCustomerProfile(unfilteredProfile));
    }
    setLoading(false);
  }, [cart, reset]);

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    setError(null);
    setLoading(true);
    const json = await getCart(locale);
    if (!json) {
      setError(
        intl.formatMessage({
          defaultMessage:
            'Es ist ein Fehler aufgetreten: Ihr Warenkorb konnte nicht geladen werden',
          id: 'lACq78',
        })
      );
      setLoading(false);
      return;
    }
    const cartObject = createCartObject(json);
    if (!cartObject?.order?.id) {
      setError(
        intl.formatMessage({
          defaultMessage:
            'Es ist ein Fehler aufgetreten: Ihr Warenkorb konnte nicht geladen werden',
          id: 'lACq78',
        })
      );
      setLoading(false);
      return;
    }
    const response = await changeAddresses(
      cartObject.order.id,
      data.billingAddress,
      data.shippingAddress,
      locale
    );

    if (!response) {
      setError(
        intl.formatMessage({
          defaultMessage:
            'Es ist ein Fehler aufgetreten: Ihre Adressdaten konnten nicht gespeichert werden',
          id: 'kO18qR',
        })
      );
      setLoading(false);
      return;
    }
    router.push('/commerce/order-detail');
  };

  return (
    <div>
      {loading ? (
        <div className="min-h-screen">
          <LoadingSpinnerWrapper>
            <LoadingSpinner size={10} />
          </LoadingSpinnerWrapper>
        </div>
      ) : (
        <Steps>
          <div>
            <h2 className="font-serif">
              <FormattedMessage defaultMessage={`Adresse`} id="D24UoX" />
            </h2>
            <h3>Daten</h3>
            <div className="mx-auto max-w-2xl px-6 lg:px-8">
              {error && <ErrorMessage message={error} />}
              <form
                className="relative"
                id="addressform"
                onSubmit={handleSubmit(onSubmit)}
              >
                {isSubmitting && (
                  <div className="absolute z-10 flex h-full w-full items-center justify-center bg-white bg-opacity-30">
                    <div className="mb-24">
                      <LoadingSpinner size={10} />
                    </div>
                  </div>
                )}
                <div className="flex flex-col gap-2 lg:gap-5">
                  <h3 className="mt-10">
                    <FormattedMessage
                      defaultMessage="Rechnungsadresse angeben"
                      id="Bx4fty"
                    />
                  </h3>
                  <RadioGroup
                    label={
                      <FormattedMessage defaultMessage={`Anrede`} id="em3wia" />
                    }
                  >
                    <Radio
                      id="salutation_mr"
                      name="salutation"
                      label={
                        <FormattedMessage defaultMessage="Herr" id="UfS7Z0" />
                      }
                      value="mr"
                      register={register('billingAddress.field_salutation')}
                      defaultChecked={
                        defaultCustomerProfile?.field_salutation === 'mr'
                      }
                    ></Radio>
                    <Radio
                      id="salutation_mrs"
                      name="salutation"
                      label={
                        <FormattedMessage defaultMessage="Frau" id="JLPcH7" />
                      }
                      value="mrs"
                      register={register('billingAddress.field_salutation')}
                      defaultChecked={
                        defaultCustomerProfile?.field_salutation === 'mrs'
                      }
                    ></Radio>
                    <Radio
                      id="salutation_neutral"
                      name="salutation"
                      label={
                        <FormattedMessage
                          defaultMessage="Neutral"
                          id="URL784"
                        />
                      }
                      value="neutral"
                      register={register('billingAddress.field_salutation')}
                      defaultChecked={
                        defaultCustomerProfile?.field_salutation === 'neutral'
                      }
                    ></Radio>
                    <Radio
                      id="salutation_company"
                      name="salutation"
                      label={
                        <FormattedMessage defaultMessage="Firma" id="8XhQag" />
                      }
                      value="company"
                      register={register('billingAddress.field_salutation')}
                      defaultChecked={
                        defaultCustomerProfile?.field_salutation === 'firma'
                      }
                    ></Radio>
                  </RadioGroup>
                  <Input
                    id="title"
                    label={
                      <FormattedMessage defaultMessage="Titel" id="oRoqyT" />
                    }
                    type="text"
                    error={errors?.billingAddress?.field_title}
                    register={register('billingAddress.field_title')}
                    defaultValue={defaultCustomerProfile?.field_title}
                  />
                  <Input
                    id="firstname"
                    label={
                      <FormattedMessage defaultMessage="Vorname" id="uzYEs0" />
                    }
                    type="text"
                    error={errors?.billingAddress?.address?.given_name}
                    register={register('billingAddress.address.given_name', {
                      required: REQUIRED_ERROR,
                    })}
                    defaultValue={defaultCustomerProfile?.address?.given_name}
                  />
                  <Input
                    id="lastname"
                    label={
                      <FormattedMessage defaultMessage="Nachname" id="E46k0V" />
                    }
                    type="text"
                    error={errors?.billingAddress?.address?.family_name}
                    register={register('billingAddress.address.family_name', {
                      required: REQUIRED_ERROR,
                    })}
                    defaultValue={defaultCustomerProfile?.address?.family_name}
                  />
                  <Input
                    id="street"
                    label={
                      <FormattedMessage defaultMessage="Strasse" id="VdyKgr" />
                    }
                    type="text"
                    error={errors?.billingAddress?.address?.address_line1}
                    register={register('billingAddress.address.address_line1', {
                      required: REQUIRED_ERROR,
                    })}
                    defaultValue={
                      defaultCustomerProfile?.address?.address_line1
                    }
                  />
                  <Input
                    id="postalcode"
                    label={
                      <FormattedMessage defaultMessage="PLZ" id="9ti+Bw" />
                    }
                    type="text"
                    error={errors?.billingAddress?.address?.postal_code}
                    register={register('billingAddress.address.postal_code', {
                      required: REQUIRED_ERROR,
                    })}
                    defaultValue={defaultCustomerProfile?.address?.postal_code}
                  />
                  <Input
                    id="location"
                    label={
                      <FormattedMessage defaultMessage="Ort" id="TZgWxf" />
                    }
                    type="text"
                    error={errors?.billingAddress?.address?.locality}
                    register={register('billingAddress.address.locality', {
                      required: REQUIRED_ERROR,
                    })}
                    defaultValue={defaultCustomerProfile?.address?.locality}
                  />
                  <CountrySelection
                    id="country"
                    label={
                      <FormattedMessage defaultMessage="Land" id="BDl566" />
                    }
                    register={register('billingAddress.address.country_code')}
                    defaultValue={defaultCustomerProfile?.address?.country_code}
                    locale={locale}
                  />
                  <Input
                    id="phonenumber"
                    label={
                      <FormattedMessage defaultMessage="Telefon" id="/m7J79" />
                    }
                    type="text"
                    error={errors?.billingAddress?.field_phone_number}
                    register={register('billingAddress.field_phone_number', {
                      required: REQUIRED_ERROR,
                    })}
                    defaultValue={defaultCustomerProfile?.field_phone_number}
                  />
                  <Input
                    id="email"
                    label={
                      <FormattedMessage defaultMessage="E-Mail" id="HEfhYI" />
                    }
                    type="email"
                    error={errors?.billingAddress?.field_email}
                    register={register('billingAddress.field_email', {
                      required: REQUIRED_ERROR,
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: EMAIL_ERROR,
                      },
                    })}
                    defaultValue={defaultCustomerProfile?.field_email}
                  />
                  <Input
                    id="dateofbirth"
                    label={
                      <FormattedMessage
                        defaultMessage="Geburtsdatum"
                        id="D7S5YM"
                      />
                    }
                    type="date"
                    error={errors?.billingAddress?.field_date_of_birth}
                    register={register('billingAddress.field_date_of_birth')}
                    defaultValue={defaultCustomerProfile?.field_date_of_birth}
                  />
                </div>
                {showShippingAddressForm && (
                  <div className="mb-8 flex flex-col gap-2 lg:gap-5">
                    <h3 className="mt-10">
                      <FormattedMessage
                        defaultMessage={`Versandadresse angeben`}
                        id="khCmeQ"
                      />
                    </h3>
                    <RadioGroup
                      label={
                        <FormattedMessage
                          defaultMessage={`Anrede`}
                          id="em3wia"
                        />
                      }
                    >
                      <Radio
                        id="salutation_mr"
                        name="salutation"
                        label={
                          <FormattedMessage defaultMessage="Herr" id="UfS7Z0" />
                        }
                        value="mr"
                        register={register('shippingAddress.field_salutation')}
                        defaultChecked={
                          defaultCustomerProfile?.field_salutation === 'mr'
                        }
                      ></Radio>
                      <Radio
                        id="salutation_mrs"
                        name="salutation"
                        label={
                          <FormattedMessage defaultMessage="Frau" id="JLPcH7" />
                        }
                        value="mrs"
                        register={register('shippingAddress.field_salutation')}
                        defaultChecked={
                          defaultCustomerProfile?.field_salutation === 'mrs'
                        }
                      ></Radio>
                      <Radio
                        id="salutation_neutral"
                        name="salutation"
                        label={
                          <FormattedMessage
                            defaultMessage="Neutral"
                            id="URL784"
                          />
                        }
                        value="neutral"
                        register={register('shippingAddress.field_salutation')}
                        defaultChecked={
                          defaultCustomerProfile?.field_salutation === 'neutral'
                        }
                      ></Radio>
                      <Radio
                        id="salutation_company"
                        name="salutation"
                        label={
                          <FormattedMessage
                            defaultMessage="Firma"
                            id="8XhQag"
                          />
                        }
                        value="company"
                        register={register('shippingAddress.field_salutation')}
                        defaultChecked={
                          defaultCustomerProfile?.field_salutation === 'firma'
                        }
                      ></Radio>
                    </RadioGroup>
                    <Input
                      id="title"
                      label={
                        <FormattedMessage defaultMessage="Titel" id="oRoqyT" />
                      }
                      type="text"
                      error={errors?.shippingAddress?.field_title}
                      register={register('shippingAddress.field_title')}
                      defaultValue={defaultCustomerProfile?.field_title}
                    />
                    <Input
                      id="firstname"
                      label={
                        <FormattedMessage
                          defaultMessage="Vorname"
                          id="uzYEs0"
                        />
                      }
                      type="text"
                      error={errors?.shippingAddress?.address?.given_name}
                      register={register('shippingAddress.address.given_name', {
                        required: REQUIRED_ERROR,
                      })}
                      defaultValue={defaultCustomerProfile?.address?.given_name}
                    />
                    <Input
                      id="lastname"
                      label={
                        <FormattedMessage
                          defaultMessage="Nachname"
                          id="E46k0V"
                        />
                      }
                      type="text"
                      error={errors?.shippingAddress?.address?.family_name}
                      register={register(
                        'shippingAddress.address.family_name',
                        {
                          required: REQUIRED_ERROR,
                        }
                      )}
                      defaultValue={
                        defaultCustomerProfile?.address?.family_name
                      }
                    />
                    <Input
                      id="street"
                      label={
                        <FormattedMessage
                          defaultMessage="Strasse"
                          id="VdyKgr"
                        />
                      }
                      type="text"
                      error={errors?.shippingAddress?.address?.address_line1}
                      register={register(
                        'shippingAddress.address.address_line1',
                        {
                          required: REQUIRED_ERROR,
                        }
                      )}
                      defaultValue={
                        defaultCustomerProfile?.address?.address_line1
                      }
                    />
                    <Input
                      id="postalcode"
                      label={
                        <FormattedMessage defaultMessage="PLZ" id="9ti+Bw" />
                      }
                      type="text"
                      error={errors?.shippingAddress?.address?.postal_code}
                      register={register(
                        'shippingAddress.address.postal_code',
                        {
                          required: REQUIRED_ERROR,
                          pattern: {
                            value: /^[0-9]{4}$/i,
                            message: POSTAL_CODE_ERROR,
                          },
                        }
                      )}
                      defaultValue={
                        defaultCustomerProfile?.address?.postal_code
                      }
                    />
                    <Input
                      id="location"
                      label={
                        <FormattedMessage defaultMessage="Ort" id="TZgWxf" />
                      }
                      type="text"
                      error={errors?.shippingAddress?.address?.locality}
                      register={register('shippingAddress.address.locality', {
                        required: REQUIRED_ERROR,
                      })}
                      defaultValue={defaultCustomerProfile?.address?.locality}
                    />
                    <CountrySelection
                      id="country"
                      label={
                        <FormattedMessage defaultMessage="Land" id="BDl566" />
                      }
                      register={register(
                        'shippingAddress.address.country_code'
                      )}
                      defaultValue={'CH'}
                      disabled={true}
                      locale={locale}
                    />
                    <Input
                      id="phonenumber"
                      label={
                        <FormattedMessage
                          defaultMessage="Telefon"
                          id="/m7J79"
                        />
                      }
                      type="text"
                      error={errors?.shippingAddress?.field_phone_number}
                      register={register('shippingAddress.field_phone_number')}
                      defaultValue={defaultCustomerProfile?.field_phone_number}
                    />
                    <Input
                      id="email"
                      label={
                        <FormattedMessage defaultMessage="E-Mail" id="HEfhYI" />
                      }
                      type="email"
                      error={errors?.shippingAddress?.field_email}
                      register={register('shippingAddress.field_email', {
                        required: REQUIRED_ERROR,
                        pattern: {
                          value: /^\S+@\S+$/i,
                          message: EMAIL_ERROR,
                        },
                      })}
                      defaultValue={defaultCustomerProfile?.field_email}
                    />
                    <Input
                      id="dateofbirth"
                      label={
                        <FormattedMessage
                          defaultMessage="Geburtsdatum"
                          id="D7S5YM"
                        />
                      }
                      type="date"
                      error={errors?.shippingAddress?.field_date_of_birth}
                      register={register('shippingAddress.field_date_of_birth')}
                      defaultValue={defaultCustomerProfile?.field_date_of_birth}
                    />
                  </div>
                )}
                <div></div>
              </form>
            </div>
          </div>
          <ArrowButton form="addressform" disabled={isSubmitting} type="submit">
            <FormattedMessage defaultMessage={`Zur Zahlung`} id="mQ3cGf" />
          </ArrowButton>
        </Steps>
      )}
    </div>
  );
}
